import { Compass, Star, Wrench, Megaphone } from 'lucide-react';
import { ShareButtons } from './ShareButtons';

interface RewardsStoreGuideContentProps {
  coverImage?: string;
  uberByPrincipalImage?: string;
}

export function RewardsStoreGuideContent({ coverImage, uberByPrincipalImage }: RewardsStoreGuideContentProps) {
  return (
    <>
      {/* Cover */}
      <section
        id="cover"
        className="px-8 py-16 md:py-24"
        style={{ background: "linear-gradient(135deg, #fef9ee 0%, #fff7e0 50%, #fef3d0 100%)", color: "#101e57" }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="hidden lg:inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6" style={{ backgroundColor: 'rgba(16,30,87,0.1)' }}>
                Field Guide
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 font-sans">
                Rewards Store Field Guide
              </h1>
              <p className="text-lg leading-relaxed" style={{ color: 'rgba(16,30,87,0.7)' }}>
                A practical, field-tested guide to building a rewards system students love — without
                burning out staff or breaking the budget.
              </p>
            </div>
            {coverImage && (
              <div className="flex-shrink-0 w-full lg:w-auto order-first lg:order-last">
                <img
                  src={coverImage}
                  alt="Illustration of a school rewards store with students and staff"
                  className="w-full max-w-xs lg:max-w-[320px] min-[1100px]:max-w-sm mx-auto"
                />
              </div>
            )}
          </div>
          <ShareButtons className="mt-8 justify-center" />
        </div>
      </section>

      {/* Introduction */}
      <section id="introduction" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <h1 className="text-3xl font-bold font-sans">Introduction</h1>
        </div>

        <h2 className="text-2xl font-bold font-sans mt-8 mb-4">
          Why Do Rewards Systems Break Down?
        </h2>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Most school rewards systems don't fail because of bad intentions.
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">They fail because:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            The store is <strong>hard to run</strong>
          </li>
          <li>
            The rewards are <strong>mispriced</strong>
          </li>
          <li>
            Staff feel like it's <strong>extra work</strong>
          </li>
          <li>Students lose interest after the novelty wears off</li>
        </ul>

        <p className="mb-4 text-gray-800 leading-relaxed">
          This guide exists for one reason:{" "}
          <strong>
            to help you build a rewards store that actually survives real school conditions.
          </strong>
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Not perfect conditions. Not high-budget conditions. Not "we have three extra staff
          members" conditions.
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">
          <strong>Real ones.</strong>
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">The Field Guide Promise</h3>

        <p className="mb-4 text-gray-800 leading-relaxed">Every tactic in this guide is:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            <strong>Actionable</strong> — you can try it this week
          </li>
          <li>
            <strong>Accessible</strong> — works with low budgets and tight schedules
          </li>
          <li>
            <strong>Specific</strong> — real examples, not theory
          </li>
        </ul>

        <div className="grid md:grid-cols-2 gap-4 my-6">
          <div className="border rounded-2xl p-5" style={{ backgroundColor: '#e6f2ec', borderColor: '#41776240' }}>
            <h4 className="font-semibold mb-3">✓ This Is</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Logistics</li>
              <li>Systems</li>
              <li>Concrete examples</li>
              <li>Real school playbooks</li>
            </ul>
          </div>
          <div className="border rounded-2xl p-5" style={{ backgroundColor: '#fce8e8', borderColor: '#dc262640' }}>
            <h4 className="font-semibold mb-3">✗ This Isn't</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Philosophy</li>
              <li>One-size-fits-all advice</li>
              <li>Expensive incentives</li>
              <li>Extra work disguised as "culture"</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Part I Header */}
      <section
        className="py-16 px-8 text-center text-white"
        style={{ background: "linear-gradient(135deg, #4786dc, #6f71ee)" }}
      >
        <Compass className="mx-auto mb-4 opacity-80" size={36} strokeWidth={1.5} />
        <p className="text-sm font-semibold uppercase tracking-wider opacity-80 mb-2">Part I</p>
        <h1 className="text-3xl font-bold font-sans text-white mb-2">Foundations</h1>
        <p className="text-lg opacity-90">Before You Stock a Single Item</p>
      </section>

      {/* Chapter 1 */}
      <section id="chapter-1" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 1
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Define the Purpose
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          If you don't define what your rewards store is <em>for</em>, it will quietly become:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>A candy dispenser</li>
          <li>A compliance tool</li>
          <li>Or a behavior band-aid</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">None of those build the culture you want.</p>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          The 3 Valid Purposes of a Rewards Store
        </h3>

        <p className="mb-4 text-gray-800 leading-relaxed">
          A strong rewards store serves <strong>one primary purpose</strong> (and maybe one
          secondary). Choose deliberately.
        </p>

        <ol className="list-decimal pl-6 mb-4 space-y-3 text-gray-800">
          <li>
            <strong>Reinforce Daily Expectations</strong>
            <br />
            <span className="text-gray-600">
              → Frequent, small rewards
              <br />→ Classroom-level focus
            </span>
          </li>
          <li>
            <strong>Drive a Specific School Goal</strong>
            <br />
            <span className="text-gray-600">
              → Attendance, transitions, hallway behavior
              <br />→ Time-bound rewards
            </span>
          </li>
          <li>
            <strong>Create Shared Culture Moments</strong>
            <br />
            <span className="text-gray-600">
              → Events, signature items, celebrations
              <br />→ Fewer but higher-impact redemptions
            </span>
          </li>
        </ol>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#e6f2ec', borderColor: '#417762' }}>
          <p className="font-semibold text-green-800 mb-2 flex items-center gap-2">
            <span>🧪</span> Field Test: Purpose in Action
          </p>
          <p className="text-gray-700">
            <strong>Attendance Problem?</strong> One school opened their store{" "}
            <strong>only on Fridays</strong> (lowest attendance day). Students had to be present
            Friday <em>and</em> Monday to receive rewards.
          </p>
          <p className="text-gray-700 mt-2">Result: Attendance rose — without adding new rewards.</p>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>Write one sentence: "Our rewards store exists to <span style={{ borderBottom: '1px solid currentColor', display: 'inline-block', width: '10rem', position: 'relative', top: '-3px' }}>&nbsp;</span>"</li>
            <li>
              If you can't finish it in under 10 seconds, pause before building anything else.
            </li>
          </ul>
        </div>
      </section>

      {/* Chapter 2 */}
      <section id="chapter-2" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 2
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Build the Right Team (Small Beats Perfect)
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          The fastest way to sink a rewards store:{" "}
          <strong>Make one person responsible for everything.</strong>
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">
          The second fastest: <strong>Put it all on admin.</strong>
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">The Smallest Team That Works</h3>

        <p className="mb-4 text-gray-800 leading-relaxed">
          You do <em>not</em> need a big committee. You need <strong>four roles</strong> — which
          can be shared or combined.
        </p>

        <ol className="list-decimal pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            <strong>Logistics Lead</strong> — Inventory, ordering, fulfillment
          </li>
          <li>
            <strong>Staff Voice</strong> — A respected teacher who gives honest feedback
          </li>
          <li>
            <strong>Student Voice</strong> — Student council, ambassadors, or store workers
          </li>
          <li>
            <strong>Decider</strong> — Someone who can decide if the group gets stuck
          </li>
        </ol>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: Student Jobs (Paid in Points)
          </p>
          <p className="text-gray-700 mb-2">
            Several schools run their stores <strong>almost entirely with students</strong>:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Cashiers</li>
            <li>Order processors</li>
            <li>Delivery runners</li>
            <li>Inventory managers</li>
          </ul>
          <p className="text-gray-700 mt-2">
            Students apply. They earn points. Admin workload drops.
          </p>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>
              Identify <strong>one student-facing role</strong> you could hand off this month.
            </li>
            <li>Start small: deliveries, promotion, or restocking.</li>
          </ul>
        </div>
      </section>

      {/* Chapter 3 */}
      <section id="chapter-3" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 3
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Set Point Guardrails
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          If you skip this step, two things will happen:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            Some teachers will give <em>tons</em> of points
          </li>
          <li>Others will give almost none</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Students will notice immediately.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Simple Guardrails That Work</h3>

        <p className="mb-4 text-gray-800 leading-relaxed">
          You don't need micromanagement. You need <strong>clear ceilings</strong>.
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">Example:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            Each day, give <strong>at least 10 points</strong>
          </li>
          <li>
            Each day, give <strong>no more than 30 points</strong>
          </li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">
          This creates fairness <em>without</em> scripting teachers.
        </p>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: Never Let Students Go "In the Hole"
          </p>
          <p className="text-gray-700 mb-2">
            Schools that subtract points for misbehavior see:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Students disengage early in the day</li>
            <li>"Why bother?" mentality</li>
          </ul>
          <p className="text-gray-700 mt-2">
            <strong>Fix:</strong> Negative behaviors = separate consequences. Positive points =
            always keepable.
          </p>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          In LiveSchool, you can configure these guardrails directly in your <a href="/features/behavior-tracking" className="text-blue-600 underline hover:text-blue-800">behavior tracking</a> settings.
        </p>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>
              Publish <strong>one slide</strong> with your point guardrails.
            </li>
            <li>If it takes more than one slide, it's too complex.</li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 my-6">
          <p className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span>📝</span> Field Note: Why Foundations Matter
          </p>
          <p className="text-gray-700 mb-2">Every school featured later in this guide:</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Defined purpose first</li>
            <li>Protected staff energy</li>
            <li>Made rewards predictable</li>
          </ul>
          <p className="text-gray-700 mt-2">
            That's what turns a rewards store from <strong>a thing you run</strong> into{" "}
            <strong>a system that runs itself.</strong>
          </p>
        </div>
      </section>

      {/* Part II Header */}
      <section
        className="py-16 px-8 text-center"
        style={{ background: "linear-gradient(135deg, #FBD56B, #f4b03d)", color: "#101e57" }}
      >
        <Star className="mx-auto mb-4 opacity-80" size={36} strokeWidth={1.5} />
        <p className="text-sm font-semibold uppercase tracking-wider opacity-80 mb-2">Part II</p>
        <h1 className="text-3xl font-bold font-sans mb-2" style={{ color: "#101e57" }}>
          Designing a Store Students Love
        </h1>
        <p className="text-lg opacity-90">Motivation Lives Here</p>
      </section>

      {/* Chapter 4 */}
      <section id="chapter-4" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 4
          </p>
          <h1 className="text-3xl font-bold font-sans">
            What to Stock (It's Not All About Stuff)
          </h1>
        </div>

        <h3 className="text-xl font-semibold mt-8 mb-3">The Two Types of Rewards</h3>

        <p className="mb-4 text-gray-800 leading-relaxed">
          The most successful stores balance both <strong>tangibles</strong> and{" "}
          <strong>experiences</strong>.
        </p>

        <div className="grid md:grid-cols-2 gap-4 my-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h4 className="font-semibold text-blue-600 mb-2">Tangibles</h4>
            <p className="text-gray-700 mb-2">Items students take home.</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Stickers, pencils, snacks</li>
              <li>School merch, water bottles</li>
              <li>Signature items (giant plush, headphones)</li>
            </ul>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h4 className="font-semibold text-blue-600 mb-2">Experiences</h4>
            <p className="text-gray-700 mb-2">Privileges and moments that feel special.</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Lunch with a friend or teacher</li>
              <li>DJ the morning announcements</li>
              <li>Sit in the principal's chair</li>
            </ul>
          </div>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Some stores lean too heavily on tangibles. Experiences cost less, create more buzz, and are harder for students to get bored of.
        </p>

        <div className="border border-gray-200 rounded-2xl p-6 my-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #fef9ee 0%, #fff7e0 100%)' }}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            {uberByPrincipalImage && (
              <img
                src={uberByPrincipalImage}
                alt="Illustration of a student being pushed in a rolling chair by a principal"
                className="w-40 md:w-48 flex-shrink-0"
              />
            )}
            <div>
              <p className="font-bold text-lg mb-2" style={{ color: '#101e57' }}>
                Example Experience: Uber by Principal
              </p>
              <p className="text-gray-700 mb-3">
                A student gets pushed through the halls in a rolling chair by an administrator. It costs nothing and students talk about it for weeks.
              </p>
              <p className="font-semibold text-gray-800 mb-2">Tips to make it legendary:</p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                <li>Pick a longer route based on the student's schedule — maximize hall time</li>
                <li>Surprise them — they know the day, but not which class they'll get pulled from</li>
                <li>Bring a speaker and let them play their favorite song on the ride</li>
                <li>Hand them sunglasses, a crown, or a fun hat before takeoff</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-8 mb-3">Rewards by Age Group</h3>

        <p className="mb-4 text-gray-800 leading-relaxed">
          What motivates a second grader won't land with an eighth grader. The same system can work across grade levels — just adjust the catalog.
        </p>

        <h4 className="text-lg font-semibold mt-6 mb-3">Elementary (K–5)</h4>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Tangibles</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Stickers, erasers, pencils</li>
              <li>Small toys, slime</li>
              <li>Bookmarks, stamps</li>
            </ul>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Experiences</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Line leader for a day</li>
              <li>Extra recess</li>
              <li>Stuffed animal desk buddy</li>
            </ul>
          </div>
        </div>

        <h4 className="text-lg font-semibold mt-6 mb-3">Middle School (6–8)</h4>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Tangibles</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Candy, snacks, drinks</li>
              <li>School merch, lanyards</li>
              <li>Wristbands, water bottles</li>
            </ul>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Experiences</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>VIP lunch table with friends</li>
              <li>DJ the morning announcements</li>
              <li>Spirit days</li>
            </ul>
          </div>
        </div>

        <h4 className="text-lg font-semibold mt-6 mb-3">High School (9–12)</h4>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Tangibles</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Gift cards, snack boxes</li>
              <li>School spirit gear</li>
              <li>Concessions at games</li>
            </ul>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-semibold text-blue-600 mb-2">Experiences</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              <li>Free period, late arrival pass</li>
              <li>Priority parking spot</li>
              <li>Staff vs. student game entry</li>
            </ul>
          </div>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>
              Ask 5 students: "If you had 200 points today, what would you actually want?"
            </li>
            <li>
              Stock <strong>what they say</strong>, not what adults assume.
            </li>
          </ul>
        </div>
      </section>

      {/* Chapter 5 */}
      <section id="chapter-5" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 5
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Pricing That Creates Motivation
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">Bad pricing kills systems quietly.</p>

        <p className="mb-4 text-gray-800 leading-relaxed">Symptoms:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Students hoard points forever</li>
          <li>Or spend immediately and disengage</li>
          <li>Or complain "nothing is affordable"</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          Price Backwards from Earning Potential
        </h3>

        <p className="mb-4 text-gray-800 leading-relaxed">Start here:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>
            How many points can a student earn <strong>per week</strong>?
          </li>
          <li>How often can they shop?</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">Then price accordingly.</p>

        <p className="mb-4 text-gray-800 leading-relaxed">Example:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>~5–10 points/day × 5 days = 25–50/week</li>
          <li>Monthly = ~100–200 points possible</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">The 4-Bucket Pricing Ladder</h3>

        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-left font-semibold">
                  Bucket
                </th>
                <th className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-left font-semibold">
                  Point Range
                </th>
                <th className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-left font-semibold">
                  Purpose
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Instant</td>
                <td className="border-b border-gray-200 px-4 py-3">5–15</td>
                <td className="border-b border-gray-200 px-4 py-3">Early success</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Weekly</td>
                <td className="border-b border-gray-200 px-4 py-3">20–40</td>
                <td className="border-b border-gray-200 px-4 py-3">Habit reinforcement</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Monthly</td>
                <td className="border-b border-gray-200 px-4 py-3">75–150</td>
                <td className="border-b border-gray-200 px-4 py-3">Goal-setting</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Signature</td>
                <td className="border-b border-gray-200 px-4 py-3">200+</td>
                <td className="border-b border-gray-200 px-4 py-3">Long-term motivation</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: "Under 50 Points" Category
          </p>
          <p className="text-gray-700 mb-2">
            Many schools add a <strong>dedicated category</strong>: "Under 50 Points"
          </p>
          <p className="text-gray-700">This:</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Guarantees early wins</li>
            <li>Prevents disengagement</li>
            <li>Helps students who need more time to earn</li>
          </ul>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <p className="text-gray-800 mb-2">Ask yourself two questions:</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>What can a new student buy after <strong>one week</strong>?</li>
            <li>What's worth saving a <strong>whole month</strong> for?</li>
          </ul>
          <p className="text-gray-800 mt-2">If you can't answer both, adjust your prices.</p>
        </div>
      </section>

      {/* Chapter 6 */}
      <section id="chapter-6" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 6
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Sourcing Rewards On a Budget
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          The number one reason schools scale back their rewards store:{" "}
          <strong>"We can't afford it."</strong>
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">
          But the best-stocked stores we've seen spend almost nothing. They source.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          4 Sourcing Channels That Work
        </h3>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 my-4">
          <h4 className="font-semibold text-blue-600 mb-2">1. Zero-Cost Rewards</h4>
          <p className="text-gray-700 mb-2">
            The most sustainable rewards cost nothing at all.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Lunch with a favorite teacher</li>
            <li>DJ the morning announcements</li>
            <li>Shoes-off pass, hat day, seat swap</li>
            <li>First in the lunch line for a week</li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 my-4">
          <h4 className="font-semibold text-blue-600 mb-2">2. Parent & PTO Drives</h4>
          <p className="text-gray-700 mb-2">
            The easiest wins. Parents <em>want</em> to help — they just need a specific ask.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Send a wish list, not a donation request</li>
            <li>Include exact items, quantities, and dollar amounts</li>
            <li>Time it with back-to-school or quarterly restock</li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 my-4">
          <h4 className="font-semibold text-blue-600 mb-2">3. Local Business Partnerships</h4>
          <p className="text-gray-700 mb-2">
            Small businesses say yes more than you'd expect — especially if you make it easy.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Ask for gift cards, coupons, or leftover inventory</li>
            <li>Offer a "proudly supported by" shout-out at events</li>
            <li>Send students to deliver a thank-you card (they remember this)</li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 my-4">
          <h4 className="font-semibold text-blue-600 mb-2">4. Corporate Donation Programs</h4>
          <p className="text-gray-700 mb-2">
            Many chains have formal school donation programs. You just have to apply.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Target, Walmart, and Dollar Tree all have school giving programs</li>
            <li>Book publishers donate overstock</li>
            <li>Sports teams donate branded merchandise</li>
          </ul>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>
              Write a <strong>10-item wish list</strong> with prices and links
            </li>
            <li>Identify <strong>3 places</strong> to share it (parents, local businesses, social media)</li>
            <li>Add three zero-cost rewards to your store today</li>
          </ul>
        </div>
      </section>

      {/* Part III Header */}
      <section
        className="py-16 px-8 text-center text-white"
        style={{ background: "linear-gradient(135deg, #417762, #4786dc)" }}
      >
        <Wrench className="mx-auto mb-4 opacity-80" size={36} strokeWidth={1.5} />
        <p className="text-sm font-semibold uppercase tracking-wider opacity-80 mb-2">
          Part III
        </p>
        <h1 className="text-3xl font-bold font-sans text-white mb-2">Logistics</h1>
        <p className="text-lg opacity-90">
          How the Store Actually Runs (Without Burning People Out)
        </p>
      </section>

      {/* Chapter 7 */}
      <section id="chapter-7" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 7
          </p>
          <h1 className="text-3xl font-bold font-sans">Store Models That Match Reality</h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          There is no "best" store model. There is only:{" "}
          <strong>The model your building can actually sustain.</strong>{" "}
          LiveSchool's <a href="/features/rewards-store" className="text-blue-600 underline hover:text-blue-800">rewards store</a> supports all of the models below.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">The 5 Proven Store Models</h3>

        {[
          {
            title: "1. Classroom Store",
            bestFor: "Teachers who want to run their own rewards",
            howItWorks: [
              "Teacher sets rewards + prices",
              "Weekly or biweekly redemption",
              "Students always have access somewhere",
            ],
            pros: ["High teacher autonomy", "No central bottleneck", "Easy to pilot"],
            cons: ["Inconsistent across classrooms", "Requires teacher buy-in"],
          },
          {
            title: "2. Mobile Cart",
            bestFor: "Elementary, or schools with limited space",
            howItWorks: [
              "Cart rolls class-to-class",
              "Items labeled with point costs",
              "Run by staff, parents, or volunteers",
            ],
            pros: ["No hallway traffic", "Creates fun moments", "Works without a dedicated room"],
            cons: ["Someone has to manage the cart", "Limited selection"],
          },
          {
            title: "3. Central Physical Store",
            bestFor: "Schools with staffing support",
            howItWorks: [
              "Fixed location in the building",
              "Scheduled grade-level shopping",
              "Can be student or staff managed",
            ],
            pros: ["Exciting, real-store feel", "Students can browse"],
            cons: ["Bottlenecks if not staggered", "Needs dedicated space"],
          },
          {
            title: "4. Online Order + Delivery",
            bestFor: "Tight schedules, small teams",
            howItWorks: [
              "Students order digitally",
              "Rewards delivered next day",
              "No store space needed",
            ],
            pros: ["Predictable workload", "No foot traffic", "Easy to scale"],
            cons: ["Delivery takes staff time"],
          },
          {
            title: "5. Online Order + Pickup",
            bestFor: "Schools with a central location",
            howItWorks: [
              "Students order digitally",
              "Pick up at a set time and location",
              "No delivery needed — students come to you",
            ],
            pros: ["Less work for staff", "Students feel ownership", "Simple logistics"],
            cons: ["Needs a pickup window"],
          },
        ].map((model, i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5 my-4">
            <h4 className="font-semibold text-blue-600 mb-3">{model.title}</h4>
            <p className="font-semibold text-gray-800 mb-3">Best for: <span className="font-normal">{model.bestFor}</span></p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-gray-800 mb-2">How it works</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {model.howItWorks.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-1">Pros</p>
                <ul className="space-y-1 text-gray-800 mb-3">
                  {model.pros.map((item, j) => (
                    <li key={j} className="flex items-start gap-2"><span className="text-green-600 mt-0.5">✓</span> {item}</li>
                  ))}
                </ul>
                <p className="font-semibold text-gray-800 mb-1">Cons</p>
                <ul className="space-y-1 text-gray-800">
                  {model.cons.map((item, j) => (
                    <li key={j} className="flex items-start gap-2"><span className="text-red-500 mt-0.5">✗</span> {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Big Idea: The Event-Based Store
          </p>
          <p className="text-gray-700 mb-2">
            Layer in a <strong>mega prize table event</strong> once a semester. Set up 10–15 tables organized by point tier, let students choose to spend or save, and make it a whole-school moment.
          </p>
          <p className="text-gray-700">
            It gives students something to look forward to beyond the day-to-day store — and creates buzz that keeps the system feeling fresh.
          </p>
        </div>

      </section>

      {/* Chapter 8 */}
      <section id="chapter-8" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 8
          </p>
          <h1 className="text-3xl font-bold font-sans">
            Running the Store
          </h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          You've picked a model. Now answer five questions — and write the answers down somewhere your whole team can see.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">When do students redeem?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Pick a consistent schedule and stick to it. Weekly, biweekly, or monthly — the cadence matters less than the consistency.
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Same day and time every cycle</li>
          <li>Students should always know when the next window is</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">Where does it happen?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          This depends on your model — a classroom, a hallway cart, the front office, or entirely online. The key is that students and staff both know where to go without asking.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Who runs it?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Assign a specific person, not just a role.
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Teachers (classroom store)</li>
          <li>A staff member or volunteer (cart, central store)</li>
          <li>Students with oversight (any model)</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">How do students get their rewards?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Browse and take? Order and pick up? Order and have it delivered? Decide once and make it predictable.
        </p>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: Split Redemption from Delivery
          </p>
          <p className="text-gray-700 mb-2">
            Many schools separate these two steps: <strong>redeem on Friday</strong>, <strong>deliver on Monday</strong>.
          </p>
          <p className="text-gray-700">
            It incentivizes Friday attendance, creates Monday momentum, and reduces same-day chaos.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-8 mb-3">What happens when something goes wrong?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          It's worth thinking through common scenarios before they happen:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>A student wants a refund</li>
          <li>An item is out of stock after a student orders it</li>
          <li>A student was absent on redemption day</li>
          <li>Two students claim the same item</li>
          <li>A teacher forgot to give points and a student missed out</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Decide how you'll handle each one <em>before</em> it happens. Write it down.
        </p>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: Zero-Out Sold Items Automatically
          </p>
          <p className="text-gray-700 mb-2">Schools that hide out-of-stock items:</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-700">
            <li>Eliminate refunds</li>
            <li>Reduce frustration</li>
            <li>Build trust</li>
          </ul>
          <p className="text-gray-700 mt-2">
            If a student spends points on something that's gone, you've broken a promise. Automate this away.
          </p>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>Answer all five questions in one sentence each</li>
            <li>Review with your team in multiple formats (an email, live in a staff meeting, etc)</li>
          </ul>
        </div>
      </section>

      {/* Chapter 9 */}
      <section id="chapter-9" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 9
          </p>
          <h1 className="text-3xl font-bold font-sans">Keeping It Fresh</h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Every rewards store hits a slump. Students get bored of the same items, teachers stop promoting it, and the energy fades. The fix isn't a bigger budget — it's variety built into the rhythm of the year.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Rotate Your Inventory</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          You don't need to restock everything at once. Swap out a few items each month so students always see something new.
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Retire items that stop getting redeemed</li>
          <li>Add seasonal rewards (holiday themes, spirit week tie-ins)</li>
          <li>Let students vote on what comes next</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">Run a Big Event Each Semester</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          A mega prize table or reward celebration once or twice a year gives students something to save for and look forward to. Keep it structured:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Set up tables organized by point level</li>
          <li>Let students choose to spend or keep saving</li>
          <li>Stagger grade levels with time slots to avoid chaos</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">Add Surprise Moments</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Unannounced rewards create buzz that planned ones can't. A few ideas:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Mystery reward of the week (revealed on Monday)</li>
          <li>Double points day with no warning</li>
          <li>A "flash sale" where one premium item drops in price for a day</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">Watch for the Mid-Year Slump</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          January through March is when most stores lose steam. Plan for it:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Schedule your biggest inventory refresh for January</li>
          <li>Plan a mid-year event or competition</li>
          <li>Ask students what they'd change — and actually do it</li>
        </ul>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>Look at your current store — which items haven't been redeemed in a month?</li>
            <li>Swap one out for something new this week</li>
          </ul>
        </div>
      </section>

      {/* Part IV Header */}
      <section
        className="py-16 px-8 text-center text-white"
        style={{ background: "linear-gradient(135deg, #6f71ee, #B721FF)" }}
      >
        <Megaphone className="mx-auto mb-4 opacity-80" size={36} strokeWidth={1.5} />
        <p className="text-sm font-semibold uppercase tracking-wider opacity-80 mb-2">Part IV</p>
        <h1 className="text-3xl font-bold font-sans text-white mb-2">Buy-In</h1>
        <p className="text-lg opacity-90">Getting People to Care (and Keep Caring)</p>
      </section>

      {/* Chapter 10 */}
      <section id="chapter-10" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 10
          </p>
          <h1 className="text-3xl font-bold font-sans">Student Buy-In (Make It Theirs)</h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          If students don't believe the store reflects <em>them</em>, they'll disengage quietly.
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">
          The fix is not better prizes. It's <strong>ownership</strong>.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          4 Ways Schools Give Students Real Ownership
        </h3>

        <h4 className="text-lg font-semibold mt-6 mb-2">1. Student-Led Promotion</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">Instead of admin announcements:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Student government visits classrooms</li>
          <li>Students explain rewards peer-to-peer</li>
          <li>Hallway signage designed by students</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">Students trust it more when it comes from peers.</p>

        <h4 className="text-lg font-semibold mt-6 mb-2">2. Student-Run Operations</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">Examples from the field:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Cashiers</li>
          <li>Inventory managers</li>
          <li>Delivery runners</li>
          <li>Marketing leads</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">Students earn points for helping.</p>

        <h4 className="text-lg font-semibold mt-6 mb-2">3. "Student Ask Tours"</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">
          PBIS leads walk classrooms asking: "If we added one thing to the store, what should it
          be?"
        </p>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Then they actually add it — and tell the student who inspired it.
        </p>

        <h4 className="text-lg font-semibold mt-6 mb-2">4. Visible Progress</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Points feel real when students can <em>see</em> them:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Dashboards on screens</li>
          <li>Printed recaps</li>
          <li>Weekly reflections</li>
        </ul>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>Pick one student group (student council, advisory)</li>
            <li>
              Give them <strong>one real decision</strong>
            </li>
            <li>Follow through publicly</li>
          </ul>
        </div>
      </section>

      {/* Chapter 11 */}
      <section id="chapter-11" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 11
          </p>
          <h1 className="text-3xl font-bold font-sans">Staff Buy-In</h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Staff resistance usually isn't philosophical. It's practical.
        </p>

        <p className="mb-4 text-gray-800 leading-relaxed">Common concerns:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>"This is one more thing"</li>
          <li>"It won't be consistent"</li>
          <li>"It won't last"</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">What Actually Builds Staff Buy-In</h3>

        <h4 className="text-lg font-semibold mt-6 mb-2">1. Guardrails, Not Scripts</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Teachers want clarity — not micromanagement.
        </p>
        <p className="mb-4 text-gray-800 leading-relaxed">Effective schools:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Set max points</li>
          <li>Define "never do" rules</li>
          <li>Leave the rest flexible</li>
        </ul>

        <h4 className="text-lg font-semibold mt-6 mb-2">2. Teacher Choice at the Classroom Level</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Schools with strong teacher participation allow teachers to:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Choose rewards</li>
          <li>Set prices</li>
          <li>Pick fulfillment routines</li>
        </ul>
        <p className="mb-4 text-gray-800 leading-relaxed">When teachers have a say, they show up for it.</p>

        <h4 className="text-lg font-semibold mt-6 mb-2">3. Recognize Teacher Effort Publicly</h4>
        <p className="mb-4 text-gray-800 leading-relaxed">Admins:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Call out teachers giving points well</li>
          <li>Share small wins in staff meetings</li>
          <li>Make effort visible</li>
        </ul>

        <div className="border-l-4 rounded-r-lg p-5 my-6" style={{ backgroundColor: '#eeedfc', borderColor: '#6f71ee' }}>
          <p className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <span>💡</span> Tip: Special Area Teachers
          </p>
          <p className="text-gray-700">
            Schools that explicitly include: Specials, Paras, Subs…see much higher consistency.
          </p>
          <p className="text-gray-700 mt-2">Even substitutes get point access.</p>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>
              Ask one respected teacher: "What would make this easier for you?"
            </li>
            <li>Fix that first.</li>
          </ul>
        </div>
      </section>

      {/* Chapter 12 */}
      <section id="chapter-12" className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
            Chapter 12
          </p>
          <h1 className="text-3xl font-bold font-sans">Using Data to Improve the Store</h1>
        </div>

        <p className="mb-4 text-gray-800 leading-relaxed">
          Data should help you adjust the system — not evaluate teachers or rank students. Check in once a month, ask three questions, and make one change.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">What to Look At</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          You don't need a dashboard. You need answers to a few simple questions:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li><strong>Points given per teacher</strong> — Are some teachers giving significantly more or fewer than others? That's a conversation, not a punishment.</li>
          <li><strong>Redemption rates</strong> — Which items are students actually choosing? Which ones sit untouched?</li>
          <li><strong>Points earned per student</strong> — Are some students falling through the cracks? Are certain grade levels or classes underrepresented?</li>
        </ul>

        <p className="mb-4 text-gray-800 leading-relaxed">
          If you're using LiveSchool, these reports are built into your <a href="/features/behavior-tracking" className="text-blue-600 underline hover:text-blue-800">behavior tracking</a> dashboard.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Where Are Students Earning?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          If most points come from one or two teachers, the system feels uneven to students. Look for gaps — not to call anyone out, but to figure out where support or encouragement is needed.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Where Are They Not Earning?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Low point activity in a class or grade level usually isn't resistance — it's a sign that something about the system doesn't fit that context. Maybe the teacher needs a simpler workflow, or the rewards don't resonate with that age group.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">What Changed When You Tried Something New?</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          This is the most useful question. When you make a change — new rewards, different schedule, a big event — look at what happened afterward. Did redemption go up? Did a different group of students start participating?
        </p>
        <p className="mb-4 text-gray-800 leading-relaxed">
          One school noticed that March always brought a spike in referrals. Instead of warnings, they planned a Spirit Week, boosted rewards, and focused on celebration. Behavior improved — not because they cracked down, but because they gave students something to look forward to.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Use Data to Time Your Moves</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Once you see patterns, you can get ahead of them:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-800">
          <li>Open the store on low-attendance days</li>
          <li>Refresh inventory before months that historically slump</li>
          <li>Target specific behaviors (hallway transitions, cafeteria) with timed incentives</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">How Often to Check</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          Monthly is enough. Weekly is overkill and leads to overreacting to normal fluctuations. Set a recurring 15-minute check-in — look at the numbers, pick one thing to adjust, and move on.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">Quick Fix Guide</h3>
        <p className="mb-4 text-gray-800 leading-relaxed">
          When something feels off, start here:
        </p>

        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-left font-semibold">
                  If this is happening…
                </th>
                <th className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-left font-semibold">
                  Try this
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Students hoarding points</td>
                <td className="border-b border-gray-200 px-4 py-3">Add cheaper items</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Staff inconsistent</td>
                <td className="border-b border-gray-200 px-4 py-3">Clarify guardrails</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Store feels chaotic</td>
                <td className="border-b border-gray-200 px-4 py-3">Stagger access by grade</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Low excitement</td>
                <td className="border-b border-gray-200 px-4 py-3">Add one signature reward</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Students disengaging</td>
                <td className="border-b border-gray-200 px-4 py-3">Ask them what they'd change</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="border-b border-gray-200 px-4 py-3">Teachers not giving points</td>
                <td className="border-b border-gray-200 px-4 py-3">Simplify &amp; refresh the expectations</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border rounded-2xl p-5 my-6" style={{ backgroundColor: '#fef6e4', borderColor: '#f4b03d40' }}>
          <p className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>⚡</span> Try This Tomorrow
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-800">
            <li>Pull up last month's data</li>
            <li>Find one thing that surprised you</li>
            <li>Make one small change based on what you see</li>
          </ul>
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-[720px] mx-auto px-8 py-12 md:py-16">
        {/* Closing */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold font-sans mb-4">
            Culture Is Built in Small, Repeatable Moments
          </h2>
          <p className="text-gray-700 mb-2">
            A rewards store doesn't create culture by itself.
          </p>
          <p className="text-gray-700 mb-4">But when designed well, it:</p>
          <ul className="space-y-2 mb-4">
            <li>✓ Makes expectations visible</li>
            <li>✓ Makes effort feel worth it</li>
            <li>✓ Makes positivity routine</li>
          </ul>
          <p className="font-semibold text-gray-800">That's how chaos becomes culture.</p>
          <ShareButtons className="mt-6 justify-center" />
        </div>
      </section>
    </>
  );
}
